import clsx from 'clsx'
import { useState } from 'react'

import Card from 'apps/employer_renewals/components/card/Card'
import CostsCard from 'apps/employer_renewals/components/costs/CostsCard'
import Header from 'apps/employer_renewals/components/Header'
import PageContainer from 'apps/employer_renewals/components/layout/PageContainer'
import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import useQuoteSummary from 'apps/employer_renewals/hooks/useQuoteSummary'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { type Period } from 'apps/employer_renewals/types'
import { Banner, useBreakpoint } from 'slab'
import { usePostEmployersRenewalsRenewalConfigsIdConfirm } from 'utils/web_api'

import BetterBenefitsSection from './BetterBenefitsSection'

type Props = {
  reviewingAcceptedQuote?: boolean
}

export default function ReviewAllowancesPage ({ reviewingAcceptedQuote }: Props) {
  const { navigateTo, goToDashboard } = useAppNavigation()
  const { breakpointIsAtLeast } = useBreakpoint()

  const { employerRenewalConfig } = useEmployerRenewalConfig()
  const { renewalApproach, employerWillOfferQmeInRenewalYear } = useAllowances()

  const allowances = employerRenewalConfig?.renewal_year_quote?.allowances ?? []
  const isOfferingDependentCoverage = allowances.some(allowance => (
    (allowance.spouse_contribution_percentage || 0) > 0 ||
      (allowance.dependent_contribution_percentage || 0) > 0 ||
      (allowance.spouse_amount?.subunits || 0) > 0 ||
      (allowance.dependent_amount?.subunits || 0) > 0
  ))

  const [period, setPeriod] = useState<Period>('annual')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const confirmAllowancesMutation = usePostEmployersRenewalsRenewalConfigsIdConfirm()

  const { currentYearCost, renewalYearCost, isLoading } = useQuoteSummary({
    renewalApproach,
    period,
  })

  const handleConfirm = () => {
    if (!employerRenewalConfig) return
    confirmAllowancesMutation.mutate({ id: employerRenewalConfig.id }, {
      onSuccess: () => {
        navigateTo(EmployerRenewalsRoute.ALLOWANCES_COMPLETE)
      },
      onError: (error: any) => {
        setErrorMessage(error.message || 'Failed to confirm allowances, please try again or contact support.')
      }
    })
  }

  const PageContainerComponent = reviewingAcceptedQuote ? 'div' : PageContainer

  return (
    <PageContainerComponent {...(!reviewingAcceptedQuote && { maxWidth: 'md' as const })}>
      {!reviewingAcceptedQuote && <Header heading={`${employerRenewalConfig?.renewal_year} renewal summary`} textAlign='left' />}
      {employerRenewalConfig
        ? (
          <CostsCard
            renewalApproach={renewalApproach}
            currentYearCost={currentYearCost}
            renewalYearCost={renewalYearCost}
            period={period}
            qmeEnabled={employerWillOfferQmeInRenewalYear}
            onPeriodChange={setPeriod}
            isLoading={isLoading}
          >
            <div className='mt-6'>
              <BetterBenefitsSection isOfferingDependentCoverage={isOfferingDependentCoverage} />
            </div>
            <div className='flex flex-col justify-end gap-3 p-6 border-t border-gray-200'>
              {errorMessage && (
                <Banner type='critical' title='Error confirming allowances'>
                  {errorMessage}
                </Banner>
              )}
              <div className={clsx('flex', {
                'justify-end': breakpointIsAtLeast('xs'),
                '[&>*]:flex-1': !breakpointIsAtLeast('xs'),
              })}
              >
                <NavigationButtons
                  onNext={reviewingAcceptedQuote ? goToDashboard : handleConfirm}
                  isLoading={confirmAllowancesMutation.isPending}
                  nextButtonText={reviewingAcceptedQuote ? 'Back to dashboard' : `Confirm my ${employerRenewalConfig.renewal_year} renewal`}
                  hideBackButton={reviewingAcceptedQuote}
                />
              </div>
            </div>
          </CostsCard>
          )
        : (
          <Card raw>
            <div className='h-72 bg-gray-200 animate-pulse' />
          </Card>
          )}
    </PageContainerComponent>
  )
}
